
import { Activity } from 'src/services/activities/activities.types';
import { rootApi } from '../rootApi';
import { ITask, DueMarker } from '../tasks/tasks.types';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import { IDeal, DealStatus, CreateDealDto, IDealPipeline, IDealDocument, ProgressDealDto, IDealCompanyLink, IDealContactLink, CreatePipelineDto, UpdatePipelineDto, AddCompanyLinksDto, AddContactLinksDto, CreateDealDocumentDto, SaveDocumentToLinkDto } from './deal-flow.types';

const dealFlowApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    // deals
    getDeal: builder.query<IDeal, { dealId: string }>({
      query: (data) => ({
        url: `/deal-flow/deals/${data.dealId}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }]
    }),
    getDealsByPipeline: builder.query<PaginatedResponse<IDeal>, { pipelineId: string; page: number; perPage: number, search_query?: string, assigned_to?: string, status?: DealStatus }>({
      query: (data) => ({
        url: `/deal-flow/pipelines/${data.pipelineId}/deals`,
        params: {
          page: data.page,
          per_page: data.perPage,
          search_query: data.search_query,
          assigned_to: data.assigned_to,
          status: data.status
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'PipelineDeals', id: arg.pipelineId }]
    }),
    getMyDealsByPipeline: builder.query<PaginatedResponse<IDeal>, { pipelineId: string; page: number; perPage: number, search_query?: string, status?: DealStatus }>({
      query: (data) => ({
        url: `/deal-flow/pipelines/${data.pipelineId}/my-deals`,
        params: {
          page: data.page,
          per_page: data.perPage,
          search_query: data.search_query,
          status: data.status
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'MyPipelineDeals', id: arg.pipelineId }]
    }),
    getAllDeals: builder.query<PaginatedResponse<IDeal>, { page: number; perPage: number, searchQuery: string, status?: DealStatus }>({
      query: (data) => ({
        url: `/deal-flow/deals`,
        params: {
          page: data.page,
          per_page: data.perPage,
          search_query: data.searchQuery,
          status: data.status
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Deals' }]
    }),
    createDeal: builder.mutation<APIResponse, CreateDealDto>({
      query: (data) => ({
        url: `/deal-flow/deals`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals' }, { type: 'PipelineDeals', id: arg.pipeline_id }, { type: 'MyPipelineDeals', id: arg.pipeline_id }],
    }),
    updateDeal: builder.mutation<APIResponse, { dealId: string; data: Partial<CreateDealDto> }>({
      query: ({ dealId, data }) => ({
        url: `/deal-flow/deals/${dealId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }],
    }),
    removeDeal: builder.mutation<APIResponse, { dealId: string }>({
      query: (data) => ({
        url: `/deal-flow/deals/${data.dealId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }],
    }),
    archiveDeal: builder.mutation<APIResponse, { dealId: string }>({
      query: (data) => ({
        url: `/deal-flow/deals/${data.dealId}/archive`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }, { type: 'Deals' }],
    }),
    progressDealStage: builder.mutation<APIResponse, { dealId: string; data: ProgressDealDto }>({
      query: ({ dealId, data }) => ({
        url: `/deal-flow/deals/${dealId}/progress`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }],
    }),
    updateDealAssignee: builder.mutation<APIResponse, { dealId: string; data: { assigned_to_id: string } }>({
      query: ({ dealId, data }) => ({
        url: `/deal-flow/deals/${dealId}/assigned-to`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }],
    }),
    getDealActivities: builder.query<PaginatedResponse<Activity>, { page: number; perPage: number, dealId: string }>({
      query: (data) => ({
        url: `/deal-flow/deals/${data.dealId}/activities`,
        params: {
          page: data.page,
          per_page: data.perPage
        }
      })
    }),
    createJobForDeal: builder.mutation<APIResponse, { dealId: string; }>({
      query: ({ dealId }) => ({
        url: `/deal-flow/deals/${dealId}/create-job`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Deals', id: arg.dealId }],
    }),

    // deal documents
    getDealDocuments: builder.query<PaginatedResponse<IDealDocument>, { dealId: string, params: PageOptions }>({
      query: ({ dealId, params }) => ({
        url: `/deal-flow/deals/${dealId}/documents`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'DealDocuments', id: args.dealId }],
    }),
    createDealDocument: builder.mutation<APIResponse, { dealId: string, data: CreateDealDocumentDto }>({
      query: ({ dealId, data }) => ({
        url: `/deal-flow/deals/${dealId}/documents`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'DealDocuments', id: args.dealId }],
    }),
    removeDealDocument: builder.mutation<APIResponse, { dealId: string, dealDocId: string }>({
      query: ({ dealId, dealDocId }) => ({
        url: `/deal-flow/deals/${dealId}/documents/${dealDocId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'DealDocuments', id: args.dealId }],
    }),
    saveDocumentToLink: builder.mutation<APIResponse, { dealId: string, dealDocId: string, data: SaveDocumentToLinkDto }>({
      query: ({ dealId, dealDocId, data }) => ({
        url: `/deal-flow/deals/${dealId}/documents/${dealDocId}/save-to-link`,
        method: HTTPRequestType.POST,
        data,
      }),
    }),

    // links
    getDealCompanyLinks: builder.query<PaginatedResponse<IDealCompanyLink>, { dealId: string; page: number; perPage: number, searchQuery?: string }>({
      query: ({ dealId, page, perPage, searchQuery }) => ({
        url: `/deal-flow/deals/${dealId}/company-links`,
        method: HTTPRequestType.GET,
        params: {
          page,
          per_page: perPage,
          search_query: searchQuery
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'DealCompanyLinks', id: arg.dealId }],
    }),
    addDealCompanyLinks: builder.mutation<APIResponse, { dealId: string; data: AddCompanyLinksDto }>({
      query: ({ dealId, data }) => ({
        url: `/deal-flow/deals/${dealId}/company-links`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'DealCompanyLinks', id: arg.dealId }],
    }),
    removeDealCompanyLink: builder.mutation<APIResponse, { dealId: string; linkId: string }>({
      query: ({ dealId, linkId }) => ({
        url: `/deal-flow/deals/${dealId}/company-links/${linkId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'DealCompanyLinks', id: arg.dealId }],
    }),

    getDealContactLinks: builder.query<PaginatedResponse<IDealContactLink>, { dealId: string; page: number; perPage: number, searchQuery?: string }>({
      query: ({ dealId, page, perPage, searchQuery }) => ({
        url: `/deal-flow/deals/${dealId}/contact-links`,
        method: HTTPRequestType.GET,
        params: {
          page,
          per_page: perPage,
          search_query: searchQuery
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'DealContactLinks', id: arg.dealId }],
    }),

    // deal tasks
    getDealTasks: builder.query<PaginatedResponse<ITask>, { dealId: string; page: number; perPage: number, searchQuery?: string, dueMarker?: DueMarker }>({
      query: ({ dealId, page, perPage, searchQuery, dueMarker }) => ({
        url: `/deal-flow/deals/${dealId}/tasks`,
        method: HTTPRequestType.GET,
        params: {
          page,
          per_page: perPage,
          search_query: searchQuery,
          due_marker: dueMarker
        }
      }),
      providesTags: (_result, _error, arg) => [{ type: 'DealTasks', id: arg.dealId }],
    }),

    // pipelines
    getAllPipelines: builder.query<PaginatedResponse<IDealPipeline>, { page: number; perPage: number, searchQuery?: string }>({
      query: (data) => ({
        url: `/deal-flow/pipelines`,
        params: {
          page: data.page,
          per_page: data.perPage,
          search_query: data.searchQuery
        }
      }),
      providesTags: (_result, _error) => [{ type: 'Pipelines' }]
    }),
    getPipeline: builder.query<IDealPipeline, { pipelineId: string }>({
      query: (data) => ({
        url: `/deal-flow/pipelines/${data.pipelineId}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Pipelines', id: arg.pipelineId }]
    }),
    createPipeline: builder.mutation<APIResponse, CreatePipelineDto>({
      query: (data) => ({
        url: `/deal-flow/pipelines`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_result, _error, _arg) => [{ type: 'Pipelines' }],
    }),
    updatePipeline: builder.mutation<APIResponse, { pipelineId: string; data: UpdatePipelineDto }>({
      query: ({ pipelineId, data }) => ({
        url: `/deal-flow/pipelines/${pipelineId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Pipelines', id: arg.pipelineId }, { type: 'Pipelines' }],
    }),
    removePipeline: builder.mutation<APIResponse, { pipelineId: string }>({
      query: (data) => ({
        url: `/deal-flow/pipelines/${data.pipelineId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Pipelines', id: arg.pipelineId }],
    }),
    removePipelineStage: builder.mutation<APIResponse, { pipelineId: string; stageId: string }>({
      query: ({ pipelineId, stageId }) => ({
        url: `/deal-flow/pipelines/${pipelineId}/stages/${stageId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Pipelines', id: arg.pipelineId }],
    }),

  }),
});

export const {
  useGetDealQuery,
  useLazyGetDealQuery,
  useGetDealsByPipelineQuery,
  useGetMyDealsByPipelineQuery,

  useGetAllDealsQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useRemoveDealMutation,
  useArchiveDealMutation,
  useGetDealActivitiesQuery,
  useCreateJobForDealMutation,

  useProgressDealStageMutation,
  useUpdateDealAssigneeMutation,
  useGetDealTasksQuery,

  useGetDealCompanyLinksQuery,
  useAddDealCompanyLinksMutation,
  useRemoveDealCompanyLinkMutation,

  useGetDealContactLinksQuery,

  useGetDealDocumentsQuery,
  useCreateDealDocumentMutation,
  useRemoveDealDocumentMutation,
  useSaveDocumentToLinkMutation,

  useGetAllPipelinesQuery,
  useGetPipelineQuery,
  useCreatePipelineMutation,
  useUpdatePipelineMutation,
  useRemovePipelineMutation,
  useRemovePipelineStageMutation

} = dealFlowApi;
